import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { styled } from 'styled-components';

import { useMultiStep } from '#/components/Form/MultiStepForm/useMultiStep';
import { AnimatedCheckmark } from '#/components/Icon/AnimatedCheckmark';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { colors, cssBreakpoints, transition } from '#/theme/theme';

import type { HeaderProps } from './MultiStepFormHeader';
import { SmallHeader_Box_1 } from './SmallHeader.styled';

type Props = Pick<HeaderProps, 'steps'> & {
  logo?: ReactNode;
};
const Step = styled(
  ({
    complete,
    index,
    active,
  }: {
    complete: boolean;
    index: number;
    active: boolean;
  }) => {
    let color: string = colors.mediumContentGrey;
    if (complete) {
      color = 'white';
    } else if (active) {
      color = colors.brandBlue;
    }
    return (
      <div
        style={{
          transition: `all ${transition}`,
          display: 'flex',
          width: 24,
          height: 24,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          fontSize: 12,
          background: complete ? colors.brandBlue : 'transparent',
          color: color,
          boxShadow: `0 0 0 2px ${active || complete ? colors.brandBlue : colors.selectionGrey} inset`,
        }}
      >
        {complete ? (
          <AnimatedCheckmark colorName="selectionGrey" size={24} />
        ) : (
          index + 1
        )}
      </div>
    );
  },
)`
  /* stylelint */
`;

const StepLine = styled.div<{ $complete?: boolean }>`
  width: 25px;

  @media (min-width: ${cssBreakpoints.xsUp}) {
    width: 50px;
  }

  position: relative;
  margin-inline: 4px;
  height: 2px;
  overflow: hidden;
  background: ${colors.elementGrey};

  &::before {
    content: '';
    position: absolute;
    right: 100%;
    width: 100%;
    height: 100%;
    background: ${colors.brandBlue};
    transform: translateX(${(props) => (props.$complete ? '100%' : 0)});
    transition: all ${transition};
  }
`;

export function SmallHeader({ steps, logo }: Props) {
  const { currentStep } = useMultiStep();

  const visibleSteps = steps
    .map((step, index) => ({ ...step, index }))
    .filter((step) => !step.ignoreInNav);
  return (
    <SmallHeader_Box_1>
      <div style={{ lineHeight: 0 }}>
        {logo ?? <IdealistLogo withText height={{ mobile: 28, desktop: 52 }} />}
      </div>

      {visibleSteps.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {visibleSteps.map((step, index) => (
            <Fragment key={step.index}>
              <Step
                active={currentStep === step.index}
                complete={currentStep > step.index}
                index={index}
              />

              {index < visibleSteps.length - 1 && (
                <StepLine $complete={currentStep > step.index} />
              )}
            </Fragment>
          ))}
        </div>
      )}
    </SmallHeader_Box_1>
  );
}
